import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import {
	useExtensionList as useExtensionListExternal,
	type ForgeUIExtensionQueryOptions,
} from '@atlassian/forge-ui/provider';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail';
import { usePageSpaceKey } from '@confluence/page-context';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError';
import { fg } from '@confluence/feature-gating';

import { useExtensionsFilteredByDisplayConditions } from '../display-conditions';
import type { ForgeModules } from '../ForgeModules';
import {
	FORGE_MODULE_BYLINE,
	FORGE_MODULE_CONTENT_ACTION,
	FORGE_MODULE_CONTEXT_MENU,
	FORGE_MODULE_CUSTOM_CONTENT,
	FORGE_MODULE_SPACE_SETTINGS,
	FORGE_MODULE_SPACE_PAGE,
	FORGE_MODULE_BACKGROUND_SCRIPT,
	FORGE_MODULE_PAGE_BANNER,
} from '../ForgeModuleType';
import { isBlockedByAppAccessRule } from '../utils/appAccessRule';

import { getUseExtensionListOptions } from './getUseExtensionListOptions';
import { usePreFilteredExtensionList } from './usePreFilteredExtensionList';

const supportedAccessNarrowingModules = new Set<ForgeModules>([
	FORGE_MODULE_BACKGROUND_SCRIPT,
	FORGE_MODULE_BYLINE,
	FORGE_MODULE_CONTENT_ACTION,
	FORGE_MODULE_CONTEXT_MENU,
	FORGE_MODULE_CUSTOM_CONTENT,
	FORGE_MODULE_PAGE_BANNER,
	FORGE_MODULE_SPACE_PAGE,
	FORGE_MODULE_SPACE_SETTINGS,
]);

export type UseExtensionListProps = {
	moduleType: ForgeModules;
	queryOptions?: ForgeUIExtensionQueryOptions;
	expandAppOwner?: boolean;
	returnBlockedExtensions?: boolean;
	skipQuery?: boolean;
	spaceContextOnly?: boolean;
};

export const supportsAccessNarrowing = (moduleType: ForgeModules): boolean => {
	return supportedAccessNarrowingModules.has(moduleType);
};

export const useExtensionList = ({
	moduleType,
	queryOptions,
	expandAppOwner = false,
	returnBlockedExtensions = false,
	skipQuery = false,
	spaceContextOnly,
}: UseExtensionListProps) => {
	const fetchHook = fg('use_new_forge_extension_list_call')
		? usePreFilteredExtensionList
		: useUnfilteredExtensionList;

	return fetchHook({
		moduleType,
		queryOptions,
		expandAppOwner,
		returnBlockedExtensions,
		skipQuery,
		spaceContextOnly,
	});
};

const useUnfilteredExtensionList = ({
	moduleType,
	queryOptions,
	expandAppOwner = false,
	returnBlockedExtensions = false,
	skipQuery = false,
}: UseExtensionListProps) => {
	const { locale } = useIntl();
	const { userId, cloudId, activationId } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const { data: spaceData, loading: spaceLoading, error: spaceError } = useSpaceDetail(spaceKey);
	const { isExternalCollaborator } = useIsExternalCollaborator();

	const dataClassificationTags = spaceData?.dataClassificationTags;
	const shouldSkip =
		Boolean(skipQuery) ||
		(!userId && !fg('unskip_useextensionlist_unlicensed_support')) ||
		(Boolean(isExternalCollaborator) && !fg('unskip_useextensionlist_unlicensed_support')) ||
		Boolean(spaceLoading) ||
		Boolean(spaceError);

	// Memorize most of the computations
	const memoHasAccessNarrowingSupport = useMemo(
		() => supportsAccessNarrowing(moduleType),
		[moduleType],
	);
	const memoUseExtensionListExternalProps = useMemo(
		() =>
			getUseExtensionListOptions({
				cloudId,
				activationId,
				locale,
				dataClassificationTags,
				hasAccessNarrowingSupport: memoHasAccessNarrowingSupport,
				expandAppOwner,
				moduleType,
				queryOptions,
				skip: shouldSkip,
				skipFilter: false,
			}),
		[
			cloudId,
			activationId,
			locale,
			dataClassificationTags,
			memoHasAccessNarrowingSupport,
			expandAppOwner,
			moduleType,
			queryOptions,
			shouldSkip,
		],
	);

	const {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	} = useExtensionListExternal(memoUseExtensionListExternalProps);

	// Filter out extensions blocked by App Access Rule if necessary
	const extsDataFilteredByAppAccessRule = useMemo(
		() =>
			extsData && !returnBlockedExtensions
				? extsData.filter((ext) => !isBlockedByAppAccessRule(ext))
				: extsData,
		[extsData, returnBlockedExtensions],
	);

	useEffect(() => {
		// suppress 403 errors as we can't do much with them
		if (extsError && isUnauthorizedError(extsError)) {
			markErrorAsHandled(extsError);
		}

		// suppress 429 errors for offending users
		if (isTooManyRequestsError(extsError)) {
			markErrorAsHandled(extsError);
		}

		if (spaceError) {
			markErrorAsHandled(spaceError);
		}
	}, [extsError, spaceError]);

	const { extensions, loading, error } = useExtensionsFilteredByDisplayConditions(
		moduleType,
		extsDataFilteredByAppAccessRule,
		extsLoading || spaceLoading,
		extsError || spaceError,
	);

	return {
		extensions,
		loading,
		error,
	};
};
